import { useState } from "react";
import { api, formatMoney, formatDate, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Trash2, Plus } from "lucide-react";

// Inline-editable row for an expected/received income item.
// Click amount → edit; toggle status pill → mark received / revert; delete via trash.
// `hideSource` — when the row is rendered inside a GroupedIncomeList group
// whose header already displays the source name, we hide the source name +
// frequency sub-line and promote the DATE to the primary label so the same
// name doesn't repeat on every child row.
export function IncomeRow({ inc, onChanged, hideSource = false }) {
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(String(inc.amount));
  const [saving, setSaving] = useState(false);
  // Two-step save UX for recurring income: after the user types a new
  // amount and clicks ✓ (or hits Enter), swap the input for a scope
  // prompt asking whether to apply the change to just this occurrence
  // or the whole future series. Set to a numeric amount while the prompt
  // is showing, `null` when idle.
  const [pendingAmount, setPendingAmount] = useState(null);
  const received = inc.status === "received";
  // Projected occurrences (recurring series expanded server-side) don't
  // have a real DB row, so PATCH/DELETE against their synthetic id would
  // 404. Hide the mutation actions so users don't get "Income not found"
  // toasts when reconciling a Monthly-view row. Amount + status changes
  // should be made on the base income row (Income Sources page).
  const projected = !!inc._projected;
  const baseId = inc._base_income_id || inc.id;
  const isRecurring = ["weekly", "biweekly", "monthly"].includes(inc.frequency);

  const commitAmount = async (n) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/incomes/${baseId}`, { amount: n });
      onChanged && onChanged(data, "update");
      toast.success(`${inc.source} → all future ${formatMoney(n)}`);
      setEditing(false);
      setPendingAmount(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const commitOverrideThisOnly = async (n) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/incomes/${baseId}/override-occurrence`, {
        date: inc.expected_date,
        amount: n,
      });
      onChanged && onChanged(data, "update");
      toast.success(`${inc.source} on ${formatDate(inc.expected_date)} → ${formatMoney(n)}`);
      setEditing(false);
      setPendingAmount(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const saveAmount = async () => {
    const n = Number(amount);
    if (!Number.isFinite(n) || n < 0) { toast.error("Enter a valid amount"); return; }
    if (n === Number(inc.amount)) { setEditing(false); return; }
    // One-time (unscheduled) income has no future series — commit straight
    // through without asking for a scope.
    if (!isRecurring) { await commitAmount(n); return; }
    // Recurring — surface the scope prompt.
    setPendingAmount(n);
  };

  // Delete flow. For recurring rows we surface a scope prompt (Just this
  // one / Entire series) — deleting a single occurrence used to nuke the
  // whole series (bug 2026-02). Backed by `skip_dates` on the income
  // model. Unscheduled/one-time rows keep the direct confirm+delete.
  const [removePrompt, setRemovePrompt] = useState(false);

  const removeEntireSeries = async () => {
    setSaving(true);
    try {
      await api.delete(`/incomes/${baseId}`);
      onChanged && onChanged(inc, "delete");
      toast.success("Income removed");
      setRemovePrompt(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const removeThisOccurrence = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/incomes/${baseId}/skip-occurrence`, {
        date: inc.expected_date,
      });
      onChanged && onChanged(data, "update");
      toast.success(`${inc.source} on ${formatDate(inc.expected_date)} removed`);
      setRemovePrompt(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (isRecurring) { setRemovePrompt(true); return; }
    if (!window.confirm(`Delete "${inc.source}"?`)) return;
    setSaving(true);
    try {
      await api.delete(`/incomes/${baseId}`);
      onChanged && onChanged(inc, "delete");
      toast.success("Income removed");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  // Detect rows created by the "Mark as received" checkbox on a projected
  // occurrence. Backend sets a signature note prefix; the frontend uses
  // this to know that unchecking should DELETE the row (rather than flip
  // it to "expected") so the recurring projection cleanly reappears in
  // its place. Base recurring rows or user-created one-time expected rows
  // that were later flipped to "received" just get their status reverted.
  const isMarkedOccurrence =
    inc.frequency === "unscheduled" &&
    inc.status === "received" &&
    typeof inc.notes === "string" &&
    inc.notes.startsWith("Marked received from recurring");

  // Unified check-handler for the "Mark as received" checkbox. Handles all
  // four flows so the caller UI stays a single control:
  //   projected + unchecked → POST /mark-occurrence-received (creates row)
  //   base expected + unchecked → PATCH status=received
  //   marked-occurrence + checked → DELETE (revert cleanly)
  //   base received + checked → PATCH status=expected
  const handleReceivedToggle = async () => {
    setSaving(true);
    try {
      if (received) {
        if (isMarkedOccurrence) {
          await api.delete(`/incomes/${inc.id}`);
          onChanged && onChanged(inc, "delete");
          toast.success(`${inc.source} → un-received`);
        } else {
          const { data } = await api.patch(`/incomes/${inc.id}`, { status: "expected", received_date: "" });
          onChanged && onChanged(data, "update");
          toast.success(`${inc.source} → expected`);
        }
      } else if (projected) {
        if (!inc._base_income_id) { toast.error("Can't mark this row — no source income"); return; }
        const { data } = await api.post(
          `/incomes/${inc._base_income_id}/mark-occurrence-received`,
          { date: inc.expected_date, amount: Number(inc.amount) },
        );
        onChanged && onChanged(data, "create");
        toast.success(`${inc.source} marked received on ${formatDate(inc.expected_date)}`);
      } else {
        const { data } = await api.patch(`/incomes/${inc.id}`, { status: "received" });
        onChanged && onChanged(data, "update");
        toast.success(`${inc.source} marked received`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div
      className={`pl-4 sm:pl-6 pr-4 sm:pr-6 py-2 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 ${received ? "opacity-70" : ""}`}
      data-testid={`ppf-income-row-${inc.id}`}
    >
      {/* Label block. Layout:
           - Desktop (sm+): three inline segments — [Date] [Frequency] [Source name]
             — all in one row. Fixed widths on date + frequency keep the source
             name aligned across rows for easy scanning.
           - Mobile: stack all three segments; source name at the top with the
             date + frequency pushed underneath so long source names never
             squeeze the frequency chip. */}
      {hideSource ? (
        <div className="flex-1 min-w-0 w-full sm:w-auto">
          <div className={`text-sm font-medium truncate ${received ? "text-[#59605C] line-through" : "text-[#3E4340]"}`}>
            {formatDate(inc.expected_date)}
          </div>
        </div>
      ) : (
        <div className="flex-1 min-w-0 w-full flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-3">
          <div
            className={`text-sm font-medium whitespace-nowrap sm:w-28 shrink-0 ${received ? "text-[#59605C] line-through" : "text-[#3E4340]"}`}
            data-testid={`ppf-income-date-${inc.id}`}
          >
            {formatDate(inc.expected_date)}
          </div>
          <div
            className="text-[10px] font-semibold uppercase tracking-widest whitespace-nowrap sm:w-20 shrink-0 text-[#8A918D]"
            data-testid={`ppf-income-freq-${inc.id}`}
          >
            {inc.frequency || "one-time"}
          </div>
          <div
            className={`text-base font-bold underline decoration-[#4A6148]/40 decoration-2 underline-offset-4 truncate flex-1 min-w-0 ${received ? "text-[#59605C] line-through" : "text-[#2F4E32]"}`}
            data-testid={`ppf-income-source-${inc.id}`}
          >
            {inc.source}
          </div>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap justify-end w-full sm:w-auto">
      {editing ? (
        pendingAmount !== null ? (
          // Scope prompt — recurring rows only. Two pill buttons stack side-
          // by-side; user picks the target of the amount change.
          <div className="flex flex-col items-end gap-1" data-testid={`ppf-income-scope-${inc.id}`}>
            <div className="text-[10px] text-[#59605C] whitespace-nowrap">
              Apply <span className="font-semibold text-[#2F4E32]">{formatMoney(pendingAmount)}</span> to
            </div>
            <div className="flex items-center gap-1 flex-wrap justify-end">
              <button
                type="button"
                onClick={() => commitOverrideThisOnly(pendingAmount)}
                disabled={saving}
                className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#F1EEE7] text-[#2F4E32] hover:bg-[#E4DFD3] disabled:opacity-50 whitespace-nowrap"
                data-testid={`ppf-income-scope-one-${inc.id}`}
              >
                Just this one
              </button>
              <button
                type="button"
                onClick={() => commitAmount(pendingAmount)}
                disabled={saving}
                className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#4A6148] text-white hover:bg-[#3E5240] disabled:opacity-50 whitespace-nowrap"
                data-testid={`ppf-income-scope-future-${inc.id}`}
              >
                All future
              </button>
              <button
                type="button"
                onClick={() => { setPendingAmount(null); setEditing(false); setAmount(String(inc.amount)); }}
                className="btn-ghost !px-1.5 !py-0.5 text-xs"
                data-testid={`ppf-income-scope-cancel-${inc.id}`}
              >
                <X size={12} />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <input
              type="number" step="0.01"
              className="w-24 px-2 py-0.5 text-right rounded-md border border-[#4A6148] focus:outline-none text-sm text-[#2C302E]"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") saveAmount(); if (e.key === "Escape") { setEditing(false); setAmount(String(inc.amount)); } }}
              autoFocus
              data-testid={`ppf-income-amount-input-${inc.id}`}
            />
            <button className="btn-primary !px-1.5 !py-0.5 text-xs" onClick={saveAmount} disabled={saving} data-testid={`ppf-income-amount-save-${inc.id}`}>
              <Check size={12} />
            </button>
            <button className="btn-ghost !px-1.5 !py-0.5 text-xs" onClick={() => { setEditing(false); setAmount(String(inc.amount)); }} data-testid={`ppf-income-amount-cancel-${inc.id}`}>
              <X size={12} />
            </button>
          </div>
        )
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className={`text-sm font-semibold decoration-dotted underline-offset-2 hover:underline ${received ? "text-[#59605C]" : "text-[#2F4E32]"}`}
          title="Click to edit amount"
          data-testid={`ppf-income-amount-${inc.id}`}
        >
          +{formatMoney(inc.amount)}
        </button>
      )}
      {/* Right-side control:
          - Not received → checkbox + "Mark as received" label (checking =
            fires the appropriate create/patch flow above).
          - Already received → white "Received ✓" pill, click reverts. */}
      {received ? (
        <button
          type="button"
          onClick={handleReceivedToggle}
          disabled={saving}
          className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap bg-white text-[#59605C] hover:bg-[#DDE9D8] hover:text-[#3B5B3E] border border-[#DDE9D8] inline-flex items-center gap-1"
          title="Click to revert to expected"
          data-testid={`ppf-income-status-toggle-${inc.id}`}
        >
          <Check size={10} className="stroke-[3]" /> Received
        </button>
      ) : (
        <label
          className="inline-flex items-center gap-1.5 cursor-pointer select-none whitespace-nowrap"
          title="Check when this paycheck arrives"
          data-testid={`ppf-income-mark-label-${inc.id}`}
        >
          <input
            type="checkbox"
            checked={false}
            onChange={handleReceivedToggle}
            disabled={saving}
            className="w-4 h-4 rounded border-[#59605C] text-[#3B5B3E] focus:ring-[#3B5B3E] cursor-pointer"
            data-testid={`ppf-income-mark-checkbox-${inc.id}`}
          />
          <span className="text-[11px] font-medium text-[#59605C]">Mark as received</span>
        </label>
      )}
      {/* Delete only shown on regular editable rows. Marked-occurrence rows
          use the checkbox uncheck to delete cleanly, so no separate trash.
          Recurring rows (base OR projected) DO show the delete — the click
          opens a scope prompt so users don't accidentally nuke the whole
          series when they meant to skip one week. */}
      {!isMarkedOccurrence && (!projected || isRecurring) && (
        removePrompt ? (
          <div className="flex flex-col items-end gap-1" data-testid={`ppf-income-remove-scope-${inc.id}`}>
            <div className="text-[10px] text-[#59605C] whitespace-nowrap">Delete&hellip;</div>
            <div className="flex items-center gap-1 flex-wrap justify-end">
              <button
                type="button"
                onClick={removeThisOccurrence}
                disabled={saving}
                className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#F1EEE7] text-[#2F4E32] hover:bg-[#E4DFD3] disabled:opacity-50 whitespace-nowrap"
                data-testid={`ppf-income-remove-one-${inc.id}`}
                title={`Skip only the ${formatDate(inc.expected_date)} occurrence`}
              >
                Just this one
              </button>
              <button
                type="button"
                onClick={removeEntireSeries}
                disabled={saving}
                className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#8C3A30] text-white hover:bg-[#7A2E24] disabled:opacity-50 whitespace-nowrap"
                data-testid={`ppf-income-remove-all-${inc.id}`}
                title="Delete this recurring income and every future occurrence"
              >
                Entire series
              </button>
              <button
                type="button"
                onClick={() => setRemovePrompt(false)}
                className="btn-ghost !px-1.5 !py-0.5 text-xs"
                data-testid={`ppf-income-remove-cancel-${inc.id}`}
              >
                <X size={12} />
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button" onClick={remove} disabled={saving}
            title={isRecurring ? "Delete this occurrence or the entire series" : "Delete this income"}
            data-testid={`ppf-income-delete-${inc.id}`}
            className="text-[#8A918D] hover:text-[#8C3A30] !px-1"
          >
            <Trash2 size={12} />
          </button>
        )
      )}
      </div>
    </div>
  );
}

// Inline "add one-time income" form for gifts, side jobs, loans, etc.
// Defaults to frequency='unscheduled' and status='expected'. The date input
// is CLAMPED to the current pay period so the new income can't accidentally
// land in a different period.
export function AddOneTimeIncomeForm({ periodStart, periodEnd, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ source: "", amount: "", expected_date: periodStart });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e && e.preventDefault();
    const amt = Number(form.amount);
    if (!form.source.trim()) { toast.error("Enter a source"); return; }
    if (!Number.isFinite(amt) || amt <= 0) { toast.error("Enter a positive amount"); return; }
    if (!form.expected_date || form.expected_date < periodStart || form.expected_date > periodEnd) {
      toast.error("Date must be inside this pay period"); return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/incomes", {
        source: form.source.trim(),
        amount: amt,
        expected_date: form.expected_date,
        frequency: "unscheduled",
        status: "expected",
      });
      onCreated && onCreated(data);
      toast.success("Income added");
      setOpen(false);
      setForm({ source: "", amount: "", expected_date: periodStart });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!open) {
    return (
      <div className="pl-6 sm:pl-16 pr-4 sm:pr-6 py-2">
        <button type="button" onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1 text-xs font-semibold text-[#2F4E32] hover:text-[#1F6B2E]"
          data-testid="ppf-income-add-toggle">
          <Plus size={12} /> Add one-time income (gift, side job, loan…)
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="pl-6 sm:pl-16 pr-4 sm:pr-6 py-3 bg-white/60 border-t border-[#EBE6E0] space-y-2"
      data-testid="ppf-income-add-form">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input type="text" placeholder="Source (e.g. Freelance)"
          className="px-2 py-1 rounded-md border border-[#CDC7BB] focus:outline-none focus:border-[#4A6148] text-sm"
          value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}
          data-testid="ppf-income-add-source" />
        <input type="number" step="0.01" placeholder="Amount"
          className="px-2 py-1 rounded-md border border-[#CDC7BB] focus:outline-none focus:border-[#4A6148] text-sm"
          value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
          data-testid="ppf-income-add-amount" />
        <input type="date" min={periodStart} max={periodEnd}
          className="px-2 py-1 rounded-md border border-[#CDC7BB] focus:outline-none focus:border-[#4A6148] text-sm"
          value={form.expected_date} onChange={(e) => setForm({ ...form, expected_date: e.target.value })}
          data-testid="ppf-income-add-date" />
      </div>
      <div className="flex items-center gap-2 justify-end">
        <button type="button" className="btn-ghost !px-2 !py-1 text-xs" onClick={() => setOpen(false)}
          data-testid="ppf-income-add-cancel">Cancel</button>
        <button type="submit" className="btn-primary !px-3 !py-1 text-xs" disabled={saving}
          data-testid="ppf-income-add-save">
          {saving ? "Adding…" : "Add income"}
        </button>
      </div>
    </form>
  );
}
